import { useState, type ReactNode } from 'react'

import { Flex } from '@vtex/shoreline'
import { t } from 'i18next'

import { useCanEditBudget } from '~/modules/retailMedia/hooks/useCanEditBudget'

import BidStrength, { BidStrengthSuggestion } from './BidStrength'
import { DTOBidStrength } from './BidStrength/dto'
import EditBudgetColumn from './EditBudget'

type CostColumnContentProps = {
  data: AdsResultFormatted
  strategy?: CampaignStrategy
  costType: CostType
  canApplySuggestion?: boolean
  insideCampaign?: boolean
}

const CostColumnContent = ({
  data,
  strategy,
  costType,
  canApplySuggestion = true,
  insideCampaign = false
}: CostColumnContentProps) => {
  const [session, setSession] = useState<{
    origin?: BidEditOrigin
    initialCostOverride?: number
  } | null>(null)

  // O editor compartilhado (EditBudgetColumn/GenericEditCost) só abre quando
  // isso é true; sem essa checagem aqui, clicar na sugestão antes disso (ex.:
  // minCostsData ainda carregando, campanha somente-leitura) entra em modo
  // controlado e cai numa ViewCost sem lápis nem Cancelar (AO-721/AO-722).
  const canEditBudget = useCanEditBudget(costType)

  const bidStrengthData = DTOBidStrength({ data, costType })
  const isEdit = session !== null

  const editor = (
    <EditBudgetColumn
      data={data}
      strategy={strategy}
      costType={costType}
      isEdit={isEdit}
      origin={session?.origin}
      initialCostOverride={session?.initialCostOverride}
      onRequestEdit={() => setSession({})}
      onCancel={() => setSession(null)}
    />
  )

  if (isEdit) {
    return editor
  }

  return (
    <Flex direction="column" gap="6px">
      <Flex align="center" gap="$space-1">
        <BidStrength status={bidStrengthData?.status} costType={costType} />
        {editor}
      </Flex>
      <BidStrengthSuggestion
        suggestion={bidStrengthData?.suggestion}
        insideCampaign={insideCampaign}
        canApply={
          canApplySuggestion && canEditBudget && strategy !== 'objective_based'
        }
        onStartEdit={value =>
          setSession({
            origin: { source: 'bid_monitor', suggestedValue: value },
            initialCostOverride: value
          })
        }
      />
    </Flex>
  )
}

type CostColumnProps = {
  campaignAdType?: CampaignAdType
  strategy?: CampaignStrategy
  superAdmin?: boolean
  insideCampaign?: boolean
}
type CostColumn = (data: CostColumnProps) => SchemaItems

const isCostMasked = (data: AdsResultFormatted, costType: CostType) =>
  data?.settings?.[costType] == null &&
  data?.campaign_settings?.[costType] == null

export const cost: CostColumn = ({
  campaignAdType,
  strategy,
  superAdmin,
  insideCampaign = false
}) => {
  // Segundo parágrafo do tooltip (comparação por categoria) exige helpContent —
  // helpText só renderiza texto puro, sem separar os dois parágrafos (AO-722)
  const label = {
    cpc: {
      label: 'CPC',
      helpContent: (
        <>
          <p>{t('rm:metrics.cpc.tooltip.table')}</p>
          <p>{t('rm:metrics.cpc.tooltip.table.benchmark')}</p>
        </>
      )
    },
    cpm: {
      label: 'CPM',
      helpContent: (
        <>
          <p>{t('rm:metrics.cpm.tooltip.table')}</p>
          <p>{t('rm:metrics.cpm.tooltip.table.benchmark')}</p>
        </>
      )
    }
  } as { [key in CostType]: { label: string; helpContent: ReactNode } }

  const cpcAdTypes: CampaignAdType[] = [
    'product',
    'sponsored_brand',
    'sponsored_brand_video'
  ]
  const cpmAdTypes: CampaignAdType[] = [
    'banner',
    'banner_video',
    'banner_off_site',
    'sponsored_brand',
    'sponsored_brand_video'
  ]

  const isObjectiveBased = (
    strategyValue?: CampaignStrategy | null,
    campaignSettingsGoal?: Goals | null
  ) => strategyValue === 'objective_based' || !!campaignSettingsGoal

  const canShowBid = (
    strategyValue?: CampaignStrategy | null,
    campaignSettingsGoal?: Goals | null
  ) =>
    isObjectiveBased(strategyValue, campaignSettingsGoal) === false ||
    superAdmin

  if (campaignAdType) {
    const hasCpc = cpcAdTypes.includes(campaignAdType)
    const hasCpm = cpmAdTypes.includes(campaignAdType)

    return {
      ...(hasCpc && {
        CPC: {
          title: 'CPC',
          helpContent: label.cpc.helpContent,
          render: data => {
            if (!canShowBid(strategy, data?.campaign_settings?.goal)) {
              return '-'
            }

            if (isCostMasked(data, 'cpc')) return null

            return (
              <CostColumnContent
                data={data}
                strategy={strategy}
                costType="cpc"
                canApplySuggestion={insideCampaign}
                insideCampaign={insideCampaign}
              />
            )
          }
        }
      }),
      ...(hasCpm && {
        CPM: {
          title: 'CPM',
          helpContent: label.cpm.helpContent,
          render: data => {
            if (!canShowBid(strategy, data?.campaign_settings?.goal)) {
              return '-'
            }

            if (isCostMasked(data, 'cpm')) return null

            return (
              <CostColumnContent
                data={data}
                strategy={strategy}
                costType="cpm"
                canApplySuggestion={insideCampaign}
                insideCampaign={insideCampaign}
              />
            )
          }
        }
      })
    }
  }

  return {
    CPC: {
      title: 'CPC',
      helpContent: label.cpc.helpContent,
      render: data => {
        if (!canShowBid(strategy, data?.campaign_settings?.goal)) {
          return '-'
        }

        const hasCpc = cpcAdTypes.includes(data?.ad_type)
        return hasCpc && !isCostMasked(data, 'cpc') ? (
          <CostColumnContent
            data={data}
            strategy={strategy}
            costType="cpc"
            canApplySuggestion={insideCampaign}
            insideCampaign={insideCampaign}
          />
        ) : null
      }
    },
    CPM: {
      title: 'CPM',
      helpContent: label.cpm.helpContent,
      render: data => {
        if (!canShowBid(strategy, data?.campaign_settings?.goal)) {
          return '-'
        }

        const hasCpm = cpmAdTypes.includes(data?.ad_type)
        return hasCpm && !isCostMasked(data, 'cpm') ? (
          <CostColumnContent
            data={data}
            strategy={strategy}
            costType="cpm"
            canApplySuggestion={insideCampaign}
            insideCampaign={insideCampaign}
          />
        ) : null
      }
    }
  }
}
